import { useContext } from 'react';

import {
  EARLY_CHECKIN,
  LATE_CHECKOUT,
} from 'app/components/commons/Experience/Availability/utils';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { useCountries } from 'app/hooks/data/useCountries';
import { useExperiencesByHotelId } from 'app/hooks/data/useExperience';
import { useRooms } from 'app/hooks/data/useRooms';
import type { Bed } from 'app/typings';
import {
  BABY_COT_BED_CATEGORY_ID,
  DAY_PACKAGE_ROOM_CATEGORY_ID,
  GUEST_BED_BED_CATEGORY_ID,
} from 'app/typings';
import { ChannelManager } from 'app/typings/channelManagers';

import {
  createTaskLists,
  getPackageHasAttributeCount,
  sortTasks,
} from './Utils';

export const useTaskCenter = () => {
  const { data: countries } = useCountries();

  const { hotelInfo } = useContext(HotelContext);
  const { publishedPackages, hasAtLeastOneDayPackage } =
    useContext(LegacyHotelContext);

  const country = countries?.find((c) => c.id === hotelInfo?.countryId);

  const isMoment = hasAtLeastOneDayPackage;

  const hasChannelManager = !!(
    hotelInfo?.channelManager &&
    hotelInfo?.channelManager !== ChannelManager.NONE
  );
  const { data: rooms } = useRooms(hotelInfo?.id);
  const { data: experiences } = useExperiencesByHotelId(hotelInfo?.id);

  const filteredRooms = rooms?.filter(
    (room) => room.categoryId !== DAY_PACKAGE_ROOM_CATEGORY_ID
  );

  const pricingRuleCount = getPackageHasAttributeCount(
    publishedPackages,
    'autoDiscountRate'
  );

  const lastMinutePricingRuleCount = getPackageHasAttributeCount(
    publishedPackages,
    'lastMinuteAutoDiscountRate'
  );

  const hasBabyCot = !!(
    filteredRooms &&
    filteredRooms.length > 0 &&
    filteredRooms.some((room) =>
      room.beds.some((bed: Bed) => bed.categoryId === BABY_COT_BED_CATEGORY_ID)
    )
  );

  const hasGuestBed = !!(
    filteredRooms &&
    filteredRooms.length > 0 &&
    !!filteredRooms.some((room) =>
      room.beds.some((bed: Bed) => bed.categoryId === GUEST_BED_BED_CATEGORY_ID)
    )
  );

  const childrenAllowedCount =
    publishedPackages.filter((pkg) => pkg.childrenAllowed).length ?? 0;

  const additionalAdultPriceCount = getPackageHasAttributeCount(
    publishedPackages,
    'additionalAdultPrice'
  );

  const singleCustomerDiscountCount = getPackageHasAttributeCount(
    publishedPackages,
    'singleCustomerDiscount'
  );

  const additionalChildPriceCount = getPackageHasAttributeCount(
    publishedPackages,
    'additionalChildPrice'
  );

  const hasEarlyCheckInExp = !!experiences?.some(
    (exp) => exp.categoryId === EARLY_CHECKIN
  );

  const hasLateCheckOutExp = !!experiences?.some(
    (exp) => exp.categoryId === LATE_CHECKOUT
  );

  const autoInventoryPricingRulesCount =
    publishedPackages.filter(
      (pkg) =>
        !!hotelInfo?.stockScrapperEnabled &&
        (pkg?.lastMinuteAutoDiscountRate !== undefined ||
          pkg?.autoDiscountRate !== undefined)
    ).length ?? 0;

  const issuingAllowed = !!country?.issuingAllowed;
  const issuingActivated = !!hotelInfo?.issuingActivated;

  const packageCount = publishedPackages.length ?? 0;

  const { doneTasks, missingTasks } = createTaskLists(
    packageCount,
    hasBabyCot,
    hasGuestBed,
    hasChannelManager,
    pricingRuleCount,
    lastMinutePricingRuleCount,
    additionalAdultPriceCount,
    singleCustomerDiscountCount,
    additionalChildPriceCount,
    hasEarlyCheckInExp,
    hasLateCheckOutExp,
    autoInventoryPricingRulesCount,
    issuingAllowed,
    issuingActivated,
    childrenAllowedCount,
    isMoment
  );

  return sortTasks(doneTasks, missingTasks);
};
